// pages/tickets/index.tsx
import { createTicketColumns, TicketActionType } from "@/colDef/ticketColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import Search from "@/components/Search";
import TicketDialog from "@/components/formDialog/TicketDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeleteTicketMutation,
  useTicketsQuery,
} from "@/hooks/api/ticket.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import {
  TicketParamsType,
  TicketStatus,
  TicketType,
} from "@/services/ticket.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

const TicketsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "all">("all");

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });
  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteDialog = useAlertDialog();
  const ticketDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    ticketId: "",
    ticketData: null as TicketType | null,
  });

  const filters: TicketParamsType = useMemo(
    () => ({
      limit,
      page,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter !== "all" && { status: statusFilter }),
    }),
    [debouncedSearch, statusFilter, page, limit]
  );

  const { data, isLoading, isError, refetch } = useTicketsQuery(filters);
  const deleteMutation = useDeleteTicketMutation(filters);

  const tickets = data?.tickets ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: TicketActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to delete ticket{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        const ticketData = tickets.find((t) => t._id === payload.id) ?? null;
        ticketDialog.open({ mode: "edit", ticketId: payload.id, ticketData });
      }
    },
    [deleteDialog, ticketDialog, tickets]
  );

  const columns = useMemo(
    () => createTicketColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load tickets."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Support Tickets</h1>
        <Button
          onClick={() =>
            ticketDialog.open({
              mode: "create",
              ticketId: "",
              ticketData: null,
            })
          }
          className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
        >
          <Plus size={16} />
          Add New
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />

        <Select
          value={statusFilter}
          onValueChange={(val) => {
            setStatusFilter(val as typeof statusFilter);
            resetPage();
          }}
        >
          <SelectTrigger className="w-36 gap-2">
            <Filter size={14} className="text-gray-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<TicketType, any> columns={columns} data={tickets} />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={tickets.length}
        onPageChange={setPage}
      />

      {/* Create / Edit Dialog */}
      <TicketDialog
        mode={ticketDialog.state.mode}
        isOpen={ticketDialog.state.isOpen}
        onOpenChange={(open) => !open && ticketDialog.close()}
        filters={filters}
        ticketId={ticketDialog.state.ticketId}
        ticketData={ticketDialog.state.ticketData}
        onCancel={ticketDialog.close}
      />

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default TicketsPage;
